// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/channel/components/EmojiPicker.module.css';
import {
	getEmojiSpriteSheetLayout,
	getSpriteSheetBackground,
} from '@app/features/channel/components/emoji_picker/EmojiPickerConstants';
import EmojiPicker, {getEmojiUsageKey} from '@app/features/emoji/state/EmojiPicker';
import type {FlatEmoji} from '@app/features/emoji/types/EmojiTypes';
import type {AvailabilityCheck} from '@app/features/expressions/utils/ExpressionPermissionUtils';
import {getEmojiDisplayDataWithSkinTone} from '@app/features/expressions/utils/SkinToneUtils';
import UnicodeEmojis, {EMOJI_SPRITES} from '@app/features/expressions/utils/UnicodeEmojis';
import {loadImage} from '@app/features/messaging/utils/ImageCacheUtils';
import {getEmojiRenderUrl} from '@app/features/messaging/utils/markdown/EmojiDetector';
import {EmojiContextMenuItems} from '@app/features/ui/action_menu/items/EmojiContextMenuItems';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {isFirefoxBrowser} from '@app/features/ui/utils/NativeUtils';
import {clsx} from 'clsx';
import React, {useEffect, useImperativeHandle, useMemo, useRef} from 'react';

// Fork imports.

import {useMergeRefs} from '@app/features/app/hooks/useMergeRefs';
import {EmojiPickerReactionContext} from '@app/features/channel/components/EmojiPicker';
import EmojiPins from '@app/features/emoji/state/EmojiPins';
import ExpressionPicker from '@app/features/emoji/state/ExpressionPicker';
import Drafts from '@app/features/messaging/state/MessagingDrafts';
import {PersonaStore} from '@app/features/persona/state/PersonaStore';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {PushPinIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useContext, useState} from 'react';
import {useDrag, useDrop} from 'react-dnd';

type PickerEmojiImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
	src: string;
	alt: string;
};

const PICKER_IMAGE_RETRY_LIMIT = 3;

const PickerEmojiImage = ({src, alt, ...props}: PickerEmojiImageProps) => {
	const imageRef = useRef<HTMLImageElement | null>(null);
	const hasLoadedRef = useRef(false);
	const retriesRef = useRef(0);
	const cancelRetryRef = useRef<(() => void) | null>(null);
	useEffect(() => {
		retriesRef.current = 0;
		return () => {
			cancelRetryRef.current?.();
			cancelRetryRef.current = null;
		};
	}, [src]);
	const handleError = () => {
		if (retriesRef.current >= PICKER_IMAGE_RETRY_LIMIT) {
			return;
		}
		retriesRef.current += 1;
		cancelRetryRef.current?.();
		cancelRetryRef.current = loadImage(src, () => {
			cancelRetryRef.current = null;
			const image = imageRef.current;
			if (image != null && image.getAttribute('src') === src) {
				image.src = src;
			}
		});
	};
	const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
		const view = event.currentTarget?.ownerDocument?.defaultView ?? window;
		view.requestAnimationFrame(() => {
			if (imageRef.current == null) {
				return;
			}
			hasLoadedRef.current = true;
			imageRef.current.classList.remove(styles.emojiImageLoading);
		});
	};
	return (
		<img
			data-flx="channel.emoji-picker.emoji-renderer.picker-emoji-image.emoji-image"
			{...props}
			ref={imageRef}
			src={src}
			alt={alt}
			className={hasLoadedRef.current ? styles.emojiImage : clsx(styles.emojiImage, styles.emojiImageLoading)}
			onLoad={hasLoadedRef.current ? undefined : handleLoad}
			onError={hasLoadedRef.current ? undefined : handleError}
		/>
	);
};

const FAVORITE_EMOJI_DND_TYPE = 'FAVORITE_EMOJI';

interface FavoriteEmojiDragItem {
	key: string;
}

interface EmojiRendererProps {
	emoji: FlatEmoji;
	handleHover: (emoji: FlatEmoji | null) => void;
	handleSelect: (emoji: FlatEmoji, shiftKey?: boolean) => void;
	skinTone: string;
	getAvailability: (emoji: FlatEmoji) => AvailabilityCheck;
	shouldAnimate: boolean;
	isHighlighted?: boolean;
	shouldScrollIntoView?: boolean;
	isFavoriteRow?: boolean;
}

const EmojiRendererBase = React.forwardRef<HTMLButtonElement, EmojiRendererProps>(
	(
		{
			emoji,
			handleHover,
			handleSelect,
			skinTone,
			getAvailability,
			shouldAnimate,
			isHighlighted = false,
			shouldScrollIntoView = false,
			isFavoriteRow = false,
			...props
		},
		forwardedRef,
	) => {
		const emojiRef = useRef<HTMLButtonElement | null>(null);
		const reactionContext = useContext(EmojiPickerReactionContext);
		const resolvedChannelId = reactionContext?.channelId ?? ExpressionPicker.channelId ?? undefined;
		const draft = resolvedChannelId ? Drafts.getDraft(resolvedChannelId) : undefined;
		const effectivePersona = PersonaStore.getEffectiveReactionPersona(resolvedChannelId ?? null, null, draft);
		const isPinned = EmojiPins.isPinned(getEmojiUsageKey(emoji), effectivePersona?.id);
		useImperativeHandle(forwardedRef, () => emojiRef.current!);
		useEffect(() => {
			if (shouldScrollIntoView && emojiRef.current) {
				emojiRef.current.scrollIntoView({block: 'nearest', inline: 'nearest'});
			}
		}, [shouldScrollIntoView]);
		const availability = getAvailability(emoji);
		const customEmojiUrl = useMemo(
			() =>
				emoji.id
					? (getEmojiRenderUrl({
							id: emoji.id,
							surrogateUrl: null,
							isAnimatable: Boolean(emoji.animated),
							animated: shouldAnimate,
							jumbo: false,
						}) ?? '')
					: (emoji.url ?? ''),
			[emoji.id, emoji.animated, emoji.url, shouldAnimate],
		);

		const emojiUsageKey = useMemo(() => getEmojiUsageKey(emoji), [emoji]);
		const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);
		// A Shift press picks an emoji and leaves the picker open, so it is never a drag. Over a custom
		// emoji's image the browser would start one a few pixels in, and the click would never come.
		const pressedWithShift = useRef(false);

		const [{isDragging}, dragRef] = useDrag(
			() => ({
				type: FAVORITE_EMOJI_DND_TYPE,
				item: (): FavoriteEmojiDragItem => ({key: emojiUsageKey}),
				canDrag: () => Boolean(isFavoriteRow) && !pressedWithShift.current,
				collect: (monitor) => ({
					isDragging: monitor.isDragging(),
				}),
			}),
			[emojiUsageKey, isFavoriteRow],
		);

		const [{isOver}, dropRef] = useDrop(
			() => ({
				accept: FAVORITE_EMOJI_DND_TYPE,
				canDrop: (item: FavoriteEmojiDragItem) => Boolean(isFavoriteRow) && item.key !== emojiUsageKey,
				hover: (item: FavoriteEmojiDragItem, monitor) => {
					if (!isFavoriteRow || item.key === emojiUsageKey) {
						setDropPosition(null);
						return;
					}
					const button = emojiRef.current;
					if (!button) return;
					const boundingRect = button.getBoundingClientRect();
					const clientOffset = monitor.getClientOffset();
					if (!clientOffset) return;
					const hoverMiddleX = (boundingRect.right - boundingRect.left) / 2;
					const hoverClientX = clientOffset.x - boundingRect.left;
					const pos = hoverClientX < hoverMiddleX ? 'before' : 'after';
					setDropPosition(pos);
				},
				drop: (item: FavoriteEmojiDragItem, monitor) => {
					if (!isFavoriteRow || item.key === emojiUsageKey) return;
					const button = emojiRef.current;
					let position: 'before' | 'after' = 'before';
					if (button) {
						const boundingRect = button.getBoundingClientRect();
						const clientOffset = monitor.getClientOffset();
						if (clientOffset) {
							const hoverMiddleX = (boundingRect.right - boundingRect.left) / 2;
							const hoverClientX = clientOffset.x - boundingRect.left;
							position = hoverClientX < hoverMiddleX ? 'before' : 'after';
						}
					}
					EmojiPicker.reorderFavorite(item.key, emojiUsageKey, position);
					setDropPosition(null);
				},
				collect: (monitor) => ({
					isOver: monitor.isOver({shallow: true}) && monitor.canDrop(),
				}),
			}),
			[emojiUsageKey, isFavoriteRow],
		);

		useEffect(() => {
			if (!isOver) {
				setDropPosition(null);
			}
		}, [isOver]);

		const dragConnectorRef = useCallback(
			(node: HTMLButtonElement | null) => {
				if (isFavoriteRow) {
					dragRef(node);
				} else {
					dragRef(null);
				}
			},
			[isFavoriteRow, dragRef],
		);

		const dropConnectorRef = useCallback(
			(node: HTMLButtonElement | null) => {
				if (isFavoriteRow) {
					dropRef(node);
				} else {
					dropRef(null);
				}
			},
			[isFavoriteRow, dropRef],
		);

		const mergedButtonRef = useMergeRefs([emojiRef, dragConnectorRef, dropConnectorRef]);

		const handleClick = (e: React.MouseEvent) => {
			if (!availability.canUse) {
				e.preventDefault();
				e.stopPropagation();
				return;
			}
			if (e.altKey) {
				e.preventDefault();
				e.stopPropagation();
				EmojiPicker.toggleFavorite(getEmojiUsageKey(emoji));
				return;
			}
			handleSelect(emoji, e.shiftKey);
		};
		const handleContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			ContextMenuCommands.openFromEvent(e, (props) => (
				<EmojiContextMenuItems
					emoji={emoji}
					channelId={resolvedChannelId}
					messageId={reactionContext?.messageId}
					onClose={props.onClose}
					data-flx="channel.emoji-picker.emoji-renderer.handle-context-menu.emoji-context-menu-items"
				/>
			));
		};
		const renderButton = (children: React.ReactNode) => {
			const isDisabled = !availability.canUse;
			const showLeftIndicator = isOver && dropPosition === 'before';
			const showRightIndicator = isOver && dropPosition === 'after';
			const className = clsx(
				styles.emojiRenderer,
				isFavoriteRow && styles.favoriteEmoji,
				isHighlighted && styles.selectedEmojiRenderer,
				isDisabled && 'cursor-not-allowed',
				isDragging && styles.emojiDragging,
			);
			return (
				<FocusRing offset={-2} data-flx="channel.emoji-picker.emoji-renderer.render-button.focus-ring">
					<button
						type="button"
						tabIndex={-1}
						ref={mergedButtonRef}
						onMouseEnter={() => handleHover(emoji)}
						onMouseLeave={() => handleHover(null)}
						onMouseDownCapture={(event) => {
							pressedWithShift.current = event.shiftKey;
						}}
						onClick={handleClick}
						onContextMenu={handleContextMenu}
						className={className}
						aria-label={emoji.name}
						aria-disabled={isDisabled}
						aria-selected={isHighlighted}
						role="option"
						data-flx="channel.emoji-picker.emoji-renderer.render-button.option.click.button"
						{...props}
					>
						{showLeftIndicator && (
							<div
								className={styles.favoriteDropIndicatorLeft}
								aria-hidden={true}
								data-flx="channel.emoji-picker.emoji-renderer.favorite-drop-indicator-left"
							/>
						)}
						{showRightIndicator && (
							<div
								className={styles.favoriteDropIndicatorRight}
								aria-hidden={true}
								data-flx="channel.emoji-picker.emoji-renderer.favorite-drop-indicator-right"
							/>
						)}
						{children}
						{isPinned && (
							<div
								className={styles.pinnedBadge}
								aria-hidden={true}
								data-flx="channel.emoji-picker.emoji-renderer.pinned-badge"
							>
								<PushPinIcon
									size={remFromPx(10)}
									weight="fill"
									data-flx="channel.emoji-picker.emoji-renderer.render-button.push-pin-icon"
								/>
							</div>
						)}
					</button>
				</FocusRing>
			);
		};
		if (emoji.guildId || emoji.id) {
			const content = (
				<PickerEmojiImage
					src={customEmojiUrl}
					alt={emoji.name}
					data-flx="channel.emoji-picker.emoji-renderer.emoji-image"
				/>
			);
			return renderButton(content);
		}
		if (!emoji.useSpriteSheet) {
			return renderButton(
				<PickerEmojiImage
					src={emoji.url ?? ''}
					alt={emoji.name}
					data-flx="channel.emoji-picker.emoji-renderer.emoji-image--2"
				/>,
			);
		}
		const hasSkinTones = emoji.hasSkinTones && skinTone;
		const index = hasSkinTones ? emoji.skinToneIndex : emoji.index;
		if (isFirefoxBrowser()) {
			const {url} = getEmojiDisplayDataWithSkinTone(emoji, skinTone);
			if (url) {
				return renderButton(
					<PickerEmojiImage src={url} alt={emoji.name} data-flx="channel.emoji-picker.emoji-renderer.emoji-image--4" />,
				);
			}
		}
		if (index === undefined) {
			return renderButton(
				<PickerEmojiImage
					src={emoji.url ?? ''}
					alt={emoji.name}
					data-flx="channel.emoji-picker.emoji-renderer.emoji-image--3"
				/>,
			);
		}
		const perRow = hasSkinTones ? EMOJI_SPRITES.SkinTonePerRow : EMOJI_SPRITES.BasePerRow;
		const rows = Math.ceil((hasSkinTones ? UnicodeEmojis.skinToneSpriteCount : UnicodeEmojis.baseSpriteCount) / perRow);
		const spriteStyle = {
			backgroundImage: getSpriteSheetBackground(hasSkinTones ? skinTone : ''),
			...getEmojiSpriteSheetLayout(index, perRow, rows),
		};
		return renderButton(
			<div
				className={styles.spriteEmoji}
				style={spriteStyle}
				data-flx="channel.emoji-picker.emoji-renderer.sprite-emoji"
			/>,
		);
	},
);

// Fork: an observer, so the pin and drag state and the reacting persona stay current.
export const EmojiRenderer = observer(EmojiRendererBase);

EmojiRenderer.displayName = 'EmojiRenderer';
